import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { camerasApi } from '@/api/endpoints'
import { queryKeys } from '@/config/queryConfig'
import { SaveStatus, SectionError, SectionSkeleton } from '@/components/settings/SectionState'
import { MyPushPicker } from '@/components/cameras/MyPushPicker'
import { myPushLimitLine, myPushRequest, type MyPushValue } from '@/lib/myPush'
import { toast } from '@/lib/toast'
import type { Camera, CameraMyPush, MyPushCategory, MyPushChoice } from '@/types'

/**
 * CP 391 — "My phone alerts": the viewer's OWN push choice for this camera (Anwar 23-27 Oct: each account customises
 * its alerts). Shown to EVERY viewer — owner, people it is shared with, superusers — unlike the owner-only
 * camera-wide box (CameraAlertsSection), which applies to everyone. One person's choice never changes anyone else's.
 *
 * It saves on its own when changed (like the contacts' "Critical alerts only" switch), never through the sheet's Save
 * button: it writes its own endpoint, PUT /cameras/{id}/my-push/, which touches only the requester's row. While a save
 * is in flight the choice being saved is shown and the choices are locked; a failed save says so and the box shows
 * the stored choice again. After a save the box and the bell's per-camera list are read again (the bell follows this
 * choice, as the phone does).
 *
 * Under the choice, at most one line names what outside the box changes what arrives (lib/myPush.ts
 * myPushLimitLine), so the screen never promises alerts that will not come. Read-only, with the reason, when the
 * person is not one of the camera's users (a superuser looking at someone else's camera): the API refuses the save.
 */
export function MyPhoneAlertsSection({ camera, open = true }: { camera: Camera; open?: boolean }) {
  const { t } = useTranslation('settings')
  const queryClient = useQueryClient()
  const key = queryKeys.cameras.myPush(camera.id)

  const query = useQuery({
    queryKey: key,
    queryFn: ({ signal }) => camerasApi.getMyPush(camera.id, signal),
    enabled: open,
    staleTime: 0,   // read fresh each time the sheet opens: the limits change with settings made elsewhere
  })

  const save = useMutation({
    mutationFn: async (value: MyPushValue & { mode: MyPushChoice }) => {
      const body = myPushRequest(value)
      const res = await camerasApi.setMyPush(camera.id, body)
      // never report a choice as saved when the server answers another one
      if (res && typeof res.mode === 'string' && res.mode !== value.mode) throw new Error('my-push choice was not stored')
      // #406 — the same for the switch (an older server answers none)
      if (res && typeof res.suspicious === 'boolean' && body.suspicious !== undefined && res.suspicious !== body.suspicious) {
        throw new Error('my-push switch was not stored')
      }
      return res
    },
    onSuccess: (res, value) => {
      toast.success(t('myPush.saved'))
      queryClient.setQueryData<CameraMyPush>(key, (old) =>
        old ? { ...old, mode: value.mode, categories: myPushRequest(value).categories, suspicious: value.suspicious, ...(res?.limits ? { limits: res.limits } : {}) } : old)
      queryClient.invalidateQueries({ queryKey: key })
      // CP 391 — the bell (and PushDeadZones) apply the stored filters of this row
      queryClient.invalidateQueries({ queryKey: queryKeys.pushPreferences.cameras() })
    },
    onError: () => toast.error(t('myPush.saveFailed')),
  })

  const data = query.data
  const shown: MyPushValue | null = save.isPending && save.variables
    ? save.variables
    : data ? { mode: data.mode, categories: data.categories ?? [], suspicious: data.suspicious !== false } : null
  const line = data && shown
    ? myPushLimitLine({ mode: shown.mode, categories: shown.categories, in_audience: data.in_audience, limits: data.limits })
    : null
  const label = (cat: MyPushCategory) => t(`myPush.categories.${cat}`)

  return (
    <section className="space-y-3" aria-labelledby={`my-push-${camera.id}-title`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 id={`my-push-${camera.id}-title`} className="text-sm font-semibold">{t('myPush.title')}</h3>
          <p className="text-xs text-muted-foreground">{t('myPush.description')} {t('myPush.bellHint')}</p>
        </div>
        <SaveStatus pending={save.isPending} saved={save.isSuccess} />
      </div>

      {query.isError && !data ? (
        <SectionError onRetry={() => { void query.refetch() }} />
      ) : !data || !shown ? (
        <SectionSkeleton />
      ) : (
        <>
          <MyPushPicker
            idPrefix={`my-push-${camera.id}`}
            value={shown}
            onChange={(next) => save.mutate(next)}
            disabled={save.isPending || !data.in_audience}
            labelledBy={`my-push-${camera.id}-title`}
          />

          {shown.mode === 'custom' && (
            <p className="text-xs text-muted-foreground">{t('myPush.custom')}</p>
          )}

          {line && (
            <p
              data-testid="my-push-limit"
              className="rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400"
            >
              {line.key === 'cameraLimited'
                ? t('myPush.limits.cameraLimited', { categories: (line.categories ?? []).map(label).join(t('myPush.listSeparator')) })
                : t(`myPush.limits.${line.key}`)}
            </p>
          )}
        </>
      )}
    </section>
  )
}
